import { Box, DialogContent, Divider, Modal, Typography } from "@mui/material"
import {
  FocusContext,
  setFocus,
  useFocusable,
} from "@noriginmedia/norigin-spatial-navigation"
import React, { useCallback, useEffect, useState } from "react"
import QrCode from "react-qr-code"
import { usePageviewTracking } from "src/screens/Detail/use-pageview-tracking"
import { theme, TI } from "src/utilities/theme"

import "./Wiggle.css"
import { DGText } from "../../../components/common/DGText"
import { GenericButton } from "../../../components/common/GenericButton"
import { ProgressSpinner } from "../../../components/ProgressSpinner"
import { useMixpanel } from "../../../hooks/use-mixpanel"
import { useModalDismiss } from "../../../hooks/use-modal-dismiss"
import useSdk from "../../../hooks/use-sdk"
import useTranslate, { Namespaces } from "../../../hooks/use-translate"
import { appStore, useAuth } from "../../../store/AppStore"
import { TranslationKey } from "../../../types"
import {
  fetchReversePin,
  PinResponse,
  verifyReversePin,
} from "../../../utilities/auth"
import { s } from "../../../utilities/common"
import { captureSentryException } from "../../../utilities/sentry"
import device from "../components/device.png"

export const LoginModal = ({
  isOpen,
  ...rest
}: {
  titleOverride?: string
  isOpen: boolean
  close: () => void
  onSuccess?: () => void
}) => {
  return isOpen ? (
    <>
      <Screen isOpen={isOpen} {...rest} />
    </>
  ) : (
    <></>
  )
}

const CancelFocusKey = "LOGINMODAL:CANCEL"

const Screen = ({
  isOpen,
  close,
  onSuccess,
  titleOverride,
}: {
  isOpen: boolean
  close: () => void
  onSuccess?: () => void
  titleOverride?: string
}) => {
  const t_common = useTranslate(Namespaces.Common)
  const t = useTranslate()
  const [pin, setPin] = useState<string[]>()
  const [authResponse, setAuthResponse] = useState<PinResponse>()
  const { storeTokens } = useAuth()
  const currentUser = appStore((state) => state.currentUser)
  const tracking = appStore((state) => state.tracking)
  const sdk = useSdk()

  const trackPageview = usePageviewTracking()

  useEffect(() => {
    if (isOpen) {
      trackPageview("Login", "account/login")
    }
  }, [isOpen])

  useEffect(() => {
    // wait for user update / then close page
    if (currentUser) {
      track("login_success", {
        method: "Pin",
        display_context: "Modal",
        user_id: currentUser?.id,
      })
      sdk
        .consentTracking({
          input: {
            consent: tracking.mixpanel ? "ALLOW" : "REVOKE",
            service: "MIXPANEL",
          },
        })
        .catch((e) => {
          console.log(e)
          captureSentryException(e)
        })
      if (onSuccess) onSuccess()
      else close()
    }
  }, [currentUser])

  const setPrompt = appStore((state) => state.setPrompt)

  const track = useMixpanel()

  useEffect(() => {
    fetchReversePin().then((res) => {
      setPin(res.user_code.split(""))
      setAuthResponse(res)
      setFocus(CancelFocusKey)
    })
  }, [])

  const poll = useCallback(
    ({ device_code }: { device_code: string }) =>
      setInterval(
        async () => {
          const res = await verifyReversePin({ device_code })
          if (!res.response) return
          if (res.status !== 200) {
            if (
              res.response.code === "access_denied" ||
              res.response.code === "expired_token"
            ) {
              const message: TranslationKey<Namespaces.Common> =
                res.response.code === "access_denied"
                  ? "form_tv_auth__usercode_not_found"
                  : "form_tv_auth__usercode_expired"
              setAuthResponse(undefined)
              setPrompt({
                open: true,
                onClose: () => close(),
                onConfirm: () => close(),
                dismissible: false,
                canClose: false,
                title: t_common("form_tv_auth__usercode_label"),
                message: t_common(message),
                icon: "ALERT",
              })
            }
          } else if (
            "access_token" in res.response &&
            "refresh_token" in res.response &&
            "expires_in" in res.response
          ) {
            storeTokens({
              accessToken: res.response.access_token,
              refreshToken: res.response.refresh_token,
              expiresIn: res.response.expires_in,
            })
          }
        },
        authResponse?.interval ? authResponse.interval * 1000 : 5000,
      ),
    [authResponse],
  )

  useEffect(() => {
    let timer: ReturnType<typeof poll>
    if (authResponse?.device_code) {
      timer = poll({ device_code: authResponse.device_code })
    }
    return () => {
      if (timer) clearInterval(timer)
    }
  }, [authResponse])

  useModalDismiss(close)

  const ariaLabel = `${t("smarttv__login_step1_description")}: 
    https://www.stage-plus.com/tv. 
    ${t("smarttv__login_step2_description")}: 
    ${t("SmartTV__Pin_Code")}: ${pin}`

  const { focusKey, ref } = useFocusable({ isFocusBoundary: true })

  return (
    <Modal
      hideBackdrop
      open={isOpen}
      sx={{
        height: "100vh",
        width: "100vw",
        backgroundColor: theme.palette.primary.main,
        "& > *": { transition: "none !important" },
      }} // don't flash the screen }}
    >
      <DialogContent>
        {!(pin && authResponse?.verification_uri_complete) ? (
          <ProgressSpinner />
        ) : (
          <>
            {/* Content */}
            <FocusContext.Provider value={focusKey}>
              <Box
                ref={ref}
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  zIndex: 3,
                  position: "relative",
                  alignItems: "center",
                  mt: s("80px"),
                }}
              >
                <DGText
                  sx={{
                    color: theme.palette.white.main,
                    minHeight: s("160px"),
                    width: s("1457px"),
                  }}
                  tx={TI.H03tv}
                >
                  {titleOverride ??
                    t("smarttv__login_title_login_or_create_account")}
                </DGText>
                <Box
                  sx={{
                    height: "100%",
                    width: "100%",
                    display: "flex",
                    flexDirection: "row",
                    alignItems: "flex-start",
                    justifyContent: "center",
                    mt: s("80px"),
                  }}
                >
                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: "row",
                      flex: 1,
                      justifyContent: "flex-end",
                    }}
                  >
                    <Box
                      sx={{
                        width: s("560px"),
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "flex-start",
                      }}
                    >
                      <Box
                        sx={{
                          display: "flex",
                          flexDirection: "row",
                          alignItems: "center",
                          justifyContent: "flex-start",
                        }}
                      >
                        <CircledDigit value={1} />
                        <DGText
                          tx={TI.Subheadline04tv}
                          sx={{
                            color: theme.palette.white.main,
                            ml: s("24px"),
                          }}
                        >
                          {t("smarttv__login_step1_description")}
                        </DGText>
                      </Box>
                      <QrCode
                        size={s(260)}
                        style={{
                          width: s("261px"),
                          height: s("260px"),
                          paddingTop: s("24px"),
                          paddingBottom: s("24px"),
                          paddingLeft: s("28px"),
                          paddingRight: s("28px"),
                          backgroundColor: theme.palette.secondary.main,
                          marginTop: s("24px"),
                          marginLeft: s("79px"),
                          marginBottom: s("15px"),
                        }}
                        bgColor={theme.palette.secondary.main}
                        fgColor={"white"}
                        viewBox={`0 0 260 260`}
                        value={
                          "https://www.stage-plus.com/tv?code=" + pin?.join("")
                        }
                      />

                      <DGText
                        tx={TI.H05tv}
                        sx={{ color: theme.palette.white.main, ml: s("79px") }}
                      >
                        stage-plus.com/tv
                      </DGText>
                    </Box>
                  </Box>
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      pl: s("160px"),
                      pr: s("160px"),
                      alignSelf: "stretch",
                    }}
                  >
                    <Divider
                      orientation={"vertical"}
                      sx={{ borderColor: theme.palette.white[30] }}
                    ></Divider>
                  </Box>
                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: "row",
                      flex: 1,
                      justifyContent: "flex-start",
                    }}
                  >
                    <Box
                      sx={{
                        width: s("710"),
                        display: "flex",
                        flexDirection: "column",
                      }}
                    >
                      <Box
                        sx={{
                          display: "flex",
                          flexDirection: "row",
                          alignItems: "center",
                          justifyContent: "flex-start",
                        }}
                      >
                        <CircledDigit value={2} />
                        <DGText
                          tx={TI.Subheadline04tv}
                          sx={{
                            color: theme.palette.white.main,
                            ml: s("24px"),
                          }}
                        >
                          {t("smarttv__login_step2_description")}
                        </DGText>
                      </Box>
                      <>
                        <Box
                          sx={{
                            ml: s("80px"),
                            display: "flex",
                            flexDirection: "row",
                            mt: s("30px"),
                          }}
                        >
                          {pin.map((char, i) => (
                            <Box
                              data-testid={"pinbox_" + i}
                              key={"pinbox_" + i}
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                height: s("168px"),
                                width: s("110px"),
                                borderRadius: s("8px"),
                                backgroundColor: theme.palette.secondary.main,
                                mr:
                                  i < pin.length - 1 || char === "-"
                                    ? s("20px")
                                    : undefined,
                              }}
                            >
                              <Typography
                                sx={{
                                  color: "white",
                                  fontWeight: 600,
                                  fontSize: s("90px"),
                                  lineHeight: s("112px"),
                                  letterSpacing: 0,
                                }}
                              >
                                {char.replace("-", "")}
                              </Typography>
                            </Box>
                          ))}
                        </Box>
                      </>
                      <img
                        style={{
                          width: s("375px"),
                          marginTop: s("42px"),
                          marginLeft: s("130px"),
                        }}
                        src={device}
                        alt=""
                      />
                    </Box>
                  </Box>
                </Box>
                <GenericButton
                  focusKey={CancelFocusKey}
                  ariaLabel={ariaLabel}
                  sx={{
                    width: s("134px"),
                    height: s("73px"),
                    mt: s("92px"),
                    pr: 0,
                    pl: 0,
                  }}
                  onEnterPress={close}
                >
                  <DGText tx={TI.Subheadline07tv} sx={{ textAlign: "center" }}>
                    {t("settings__button_cancel")}
                  </DGText>
                </GenericButton>
              </Box>
            </FocusContext.Provider>
          </>
        )}
      </DialogContent>
    </Modal>
  )
}

const CircledDigit = ({ value }: { value: number }) => {
  return (
    <Box
      sx={{
        borderWidth: s("5px"),
        borderColor: "white",
        borderStyle: "solid",
        borderRadius: "50%",
        width: s("55px"),
        height: s("55px"),
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        flexShrink: 0,
      }}
    >
      <DGText tx={TI.Subheadline07tv} sx={{ color: theme.palette.white.main }}>
        {value}
      </DGText>
    </Box>
  )
}
